import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Header from "../sections/Header";
import Footer from "../sections/Footer";
import { SERVICES, BRAND } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function IntakePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    relationship_to_care_recipient: "",
    care_recipient_name: "",
    care_recipient_age: "",
    services_needed: [],
    care_frequency: "",
    start_date: "",
    address: "",
    additional_notes: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (name) =>
    setForm((f) => ({
      ...f,
      services_needed: f.services_needed.includes(name)
        ? f.services_needed.filter((s) => s !== name)
        : [...f.services_needed, name],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.relationship_to_care_recipient) {
      toast.error("Please complete the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/intake`, form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please call us at 608-513-7639.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="intake-page" className="min-h-screen bg-[#FFFFFF]">
      <Header />
      <section className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <button
            data-testid="intake-back"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#AC9667] mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {done ? (
            <div className="bg-white rounded-3xl p-10 border border-[#E5DDC9] text-center shadow-[0_30px_60px_-30px_rgba(15,23,42,0.25)]">
              <div className="inline-flex w-16 h-16 rounded-full bg-[#AC9667]/10 items-center justify-center mb-5">
                <CheckCircle2 className="w-8 h-8 text-[#AC9667]" />
              </div>
              <h1 className="font-serif text-4xl md:text-5xl text-[#005165] mb-3">Thank you.</h1>
              <p className="text-[#4B5563] max-w-md mx-auto leading-relaxed">
                Your intake has been received. A member of our care team will reach out within one
                business day. If your situation is urgent, please call us directly.
              </p>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="inline-block mt-7 px-7 py-3 rounded-full bg-[#005165] text-[#FFFFFF] btn-shimmer"
              >
                Call {BRAND.phone}
              </a>
            </div>
          ) : (
            <>
              <span className="gold-divider mb-5">Intake Form</span>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#005165] leading-[1.08]">
                Tell us about your loved one.
              </h1>
              <p className="mt-5 text-[#4B5563] text-[17px] leading-relaxed">
                This takes about 3 minutes. Everything you share is confidential and reviewed
                personally by our care team.
              </p>

              <form
                onSubmit={submit}
                data-testid="intake-form"
                className="mt-10 bg-white rounded-3xl p-8 md:p-10 border border-[#E5DDC9]/70 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.2)] space-y-7"
              >
                <fieldset className="space-y-4">
                  <legend className="font-serif text-2xl text-[#005165] mb-2">Your Info</legend>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ik-name">Your Full Name *</Label>
                      <Input id="ik-name" data-testid="ik-name" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                    <div>
                      <Label htmlFor="ik-email">Email *</Label>
                      <Input id="ik-email" data-testid="ik-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                    <div>
                      <Label htmlFor="ik-phone">Phone *</Label>
                      <Input id="ik-phone" data-testid="ik-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                    <div>
                      <Label htmlFor="ik-rel">Relationship to Care Recipient *</Label>
                      <Input id="ik-rel" data-testid="ik-rel" placeholder="e.g. Daughter, Son, Spouse" value={form.relationship_to_care_recipient} onChange={(e) => update("relationship_to_care_recipient", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                  </div>
                </fieldset>

                <fieldset className="space-y-4">
                  <legend className="font-serif text-2xl text-[#005165] mb-2">About Your Loved One</legend>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ik-cn">Their Name</Label>
                      <Input id="ik-cn" data-testid="ik-cn" value={form.care_recipient_name} onChange={(e) => update("care_recipient_name", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                    <div>
                      <Label htmlFor="ik-age">Age</Label>
                      <Input id="ik-age" data-testid="ik-age" value={form.care_recipient_age} onChange={(e) => update("care_recipient_age", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="ik-addr">Address / Neighborhood (Madison area)</Label>
                      <Input id="ik-addr" data-testid="ik-addr" value={form.address} onChange={(e) => update("address", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                  </div>
                </fieldset>

                <fieldset className="space-y-4">
                  <legend className="font-serif text-2xl text-[#005165] mb-2">Services Needed</legend>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {SERVICES.map((s) => (
                      <label
                        key={s.title}
                        data-testid={`ik-svc-${s.title.replace(/\s/g, '-').toLowerCase()}`}
                        className="flex items-center gap-3 p-3 rounded-xl border border-[#E5DDC9] bg-[#FFFFFF] hover:border-[#AC9667]/50 cursor-pointer"
                      >
                        <Checkbox
                          checked={form.services_needed.includes(s.title)}
                          onCheckedChange={() => toggleService(s.title)}
                          className="border-[#AC9667] data-[state=checked]:bg-[#AC9667] data-[state=checked]:text-white"
                        />
                        <span className="text-sm text-[#005165]">{s.title}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="space-y-4">
                  <legend className="font-serif text-2xl text-[#005165] mb-2">Care Details</legend>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ik-freq">Care Frequency</Label>
                      <Select value={form.care_frequency} onValueChange={(v) => update("care_frequency", v)}>
                        <SelectTrigger id="ik-freq" data-testid="ik-freq" className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]">
                          <SelectValue placeholder="Select frequency" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="A few hours/week">A few hours/week</SelectItem>
                          <SelectItem value="Daily (part-time)">Daily (part-time)</SelectItem>
                          <SelectItem value="Daily (full-time)">Daily (full-time)</SelectItem>
                          <SelectItem value="24/7 Live-in">24/7 Live-in</SelectItem>
                          <SelectItem value="Respite (occasional)">Respite (occasional)</SelectItem>
                          <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="ik-start">Desired Start Date</Label>
                      <Input id="ik-start" data-testid="ik-start" type="date" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="ik-notes">Anything else you'd like us to know?</Label>
                      <Textarea id="ik-notes" data-testid="ik-notes" rows={4} value={form.additional_notes} onChange={(e) => update("additional_notes", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
                    </div>
                  </div>
                </fieldset>

                <Button
                  data-testid="ik-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto bg-[#005165] hover:bg-[#003946] text-[#FFFFFF] rounded-full h-13 px-10 py-4 btn-shimmer"
                >
                  {loading ? "Submitting..." : (<><Heart className="w-4 h-4 mr-2" /> Submit Intake</>)}
                </Button>
              </form>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
